import type {
  InstantBonusClearanceType,
  NotificationType
} from '@swf/components/StarsMPNotification/enums';
import type { Currency, SuspendAccountCode } from '@swf/core/enums';

export interface BaseNotification {
  submitted?: boolean;
  hasError?: boolean;
  clearbets?: number;
  notificationId: number;
  rollId: number | string;
}

export interface Counter {
  freeBetCounter?: number;
  freeCardCounter?: number;
  freeSpinCounter?: number;
  instantBonusCounter?: number;
  ispFreeBetsCounter?: number;
  prizeGameCounter?: number;
  sportsCashBonusCounter?: number;
  releaseBonusCounter?: number;
}

export interface LoginEventNotification {
  event: number;
  param?: number;
  rmOk?: boolean;
  gracePeriod?: number;
}

export interface IssuedGameTicket extends BaseNotification {
  expiredDate?: string;
  expiresIn: { days?: number; hours: number; minutes: number };
  fromStartToExpiry?: { days?: number; hours: number; minutes: number };
  gameTicketCost?: number;
  gameTicketCostFpp1?: number;
  gameTicketId: number;
  gameTicketName?: string;
  playsInitial?: number;
  status: number;
  timeZone?: string;
}

export interface ChestNotification extends BaseNotification {
  chestCount?: number;
  chestToShow?: {
    isExpired?: boolean;
    expiresOn?: string;
    chestDescription?: string;
    chestText?: string;
    logoName?: string;
    skinType?: string;
    customSkin?: string;
  };
  clientReqId?: number;
  lockedChestCount?: number;
  lockedChestToShow?: {
    isExpired?: boolean;
    expiresOn?: string;
    chestDescription?: string;
    chestText?: string;
    logoName?: string;
    skinType?: string;
    customSkin?: string;
    lockType: string;
    props: string;
  };
  userOptedIn?: boolean;
}

export interface ChestTierUpDownNotification extends BaseNotification {
  tierId: number;
  cvl2: boolean;
  boostEnabled: boolean;
  showTierDownExchange: boolean;
  showTierUpPopUp: boolean;
  showTierDownPopUp: boolean;
  goingToTierUp: boolean;
  goingToTierDown: boolean;
  canExchange: boolean;
  tierUpDays: number;
  tierUpBars: number;
  daysToExpire: number;
  exchangePercent: number;
  expiresOn: string;
  recentBarCounter: number;
  tierDownDays: number;
  startedOn: string;
  totalDoneBarCount: number;
  timezone: string;
}

export interface FreeSpinsNotification extends BaseNotification {
  clearBets?: number;
  currency?: Currency;
  description?: string;
  expiredDate?: string;
  expiresIn?: { hours: number; minutes: number };
  formattedRedemptionLimit?: string;
  redemptionLimit?: number;
  rewardAssetType?: number;
  status: number;
  timeZone?: string;
}

export enum NotificationCode {
  Info = 0,
  Warning = 1,
  Error = 2,
  FirstDepositInfo = 3,
  SessionInfo = 4
}

export interface LobbyNotification {
  msg: string;
  notificationCode: number;
}

export interface BonusNotification extends BaseNotification {
  amountToPay?: number;
  amountToPayFormatted?: string;
  clearBets: number;
  clearPoints?: string;
  clearStakes?: number;
  currency?: Currency;
  currentAmount: number;
  description?: string;
  expiredDate: string;
  expiresIn: { hours: number; minutes: number };
  formattedClearStakes?: string;
  formattedCurrentAmount?: string;
  formattedInitialAmount?: string;
  formattedRedemptionLimit?: string;
  initialAmount: number;
  isCasino?: boolean;
  isPoker?: boolean;
  isSplitWager?: boolean;
  isSports?: boolean;
  longEligibleGameNames?: string;
  paidCash: number;
  redemptionLimit?: number;
  redemptionLimitReached?: boolean;
  shortEligibleGameNames?: string;
  status: number;
  timeZone?: string;
  costFpp?: number;
  costCash?: number;
  formattedCostCash?: string;
}

export interface GameTypesAndVariants {
  C: { T: number; V_?: { V: number }[] }[];
}
export interface SplitWagerBonusMPNotification extends BaseMPNotification {
  rollState: number;
  attributes: {
    clearanceType: InstantBonusClearanceType;
    bonusName: string;
    userRollId: number;
    currencyCode: Currency;
    initialAmount: number;
    costCash: number;
    costRatio: number;
    topUpAdded: number;
    topUpRatio: number;
    gameMaxBet?: number;
    maxWinnings?: number;
    longEligibleGameNames: string;
    shortEligibleGameNames: string;
    supportedGamesJson: string | GameTypesAndVariants[];
    betsToClear: number;
    pointsToClear: number;
    stakesToClear: number;
    expiryDate: string;
    notificationId: number;
    rollState: number;
  };
}

export interface BaseMPNotification extends BaseNotification {
  type: NotificationType;
  state?: string;
  title?: string;
  description?: string;
  moreInfo?: string;
  headerImageURL?: string;
}

export interface GameTicketMPNotification extends BaseMPNotification {
  attributes: {
    gameTicketName?: string;
    minigameId?: string;
    playsAvailable?: number;
    expiration?: string;
    userPreferredTimeZone: number;
    minigameType: number;
    ticketTypeId: number;
    ticketId: number;
  };
}

export interface BaseMPNotification extends BaseNotification {
  type: NotificationType;
  state?: string;
  title?: string;
  description?: string;
  moreInfo?: string;
  headerImageURL?: string;
}

export interface DisplayValuePropositionNotification extends BaseNotification {
  selfExclusionExpirationDate: string | null;
}

export interface FixedPrizeWinner {
  prizeType: string;
  playerId: string;
}

export interface FixedPrize {
  prizeName: string;
  prizeType: string;
  launchURL: string;
  imageURL?: string;
}

export interface FixedPrizeDrawIssuedMPNotification extends BaseMPNotification {
  attributes: {
    entries: number;
    draw: string;
    entryId: number;
    drawId: number;
  };
}

export interface FixedPrizeDrawWonMPNotification extends BaseMPNotification {
  attributes: {
    numPrizes: number;
    numWinners: number;
    entryId: number;
    drawId: number;
    winners: FixedPrizeWinner[];
    prizes: FixedPrize[];
    myPrizes: number;
    drawName?: string;
  };
}

export interface FixedPrizeDrawLostMPNotification extends BaseMPNotification {
  attributes: {
    numPrizes: number;
    numWinners: number;
    draw: string;
    entryId: number;
    drawId: number;
    winners: FixedPrizeWinner[];
  };
}

export interface FixedPrizeWinner {
  prizeType: string;
  playerId: string;
}

export interface FixedPrizeDrawMPNotification extends BaseMPNotification {
  attributes: {
    numPrizes?: number;
    numWinners?: number;
    entries?: number;
    draw?: string;
    userPreferredTimeZone: number;
    entryId: number;
    drawId: number;
    winners?: FixedPrizeWinner[];
    prizes?: FixedPrize[];
  };
}

export interface URIHint {
  path: string;
}

export interface AssetMetaData {
  type: number;
  id: number;
  uriHint: string | URIHint;
}

export interface ChestRewardMPNotification extends BaseMPNotification {
  hideTimes: boolean;
  attributes: {
    chestId: number;
    logoName?: string;
    customSkin?: string;
    icePromoId?: number;
    numberOfChests?: number;
    expiresOn?: string;
    availableOn?: string;
    metaData: AssetMetaData[];
  };
}

export interface InactivityNotification extends BaseNotification {
  idleSeconds?: number;
  remainingSeconds?: number;
}

export interface SeUpdateNotification extends BaseNotification {
  selfExclusionEndDate: string;
}

export interface InactivityMessage {
  notification?: InactivityNotification;
  reasonCode?: number;
  idleSeconds?: number;
  text?: string;
}

export type RawNotification = Notification & {
  rollid: number;
  rollflags?: number;
  status?: number;
  notificationid: number;
  clearbets?: number;
};

export type MultiPurposeNotification =
  | GameTicketMPNotification
  | ChestRewardMPNotification
  | FixedPrizeDrawIssuedMPNotification
  | FixedPrizeDrawWonMPNotification
  | FixedPrizeDrawLostMPNotification
  | SplitWagerBonusMPNotification;

export type Notification =
  | FreeSpinsNotification
  | ChestNotification
  | ChestTierUpDownNotification
  | BonusNotification
  | InactivityNotification
  | GameTicketMPNotification
  | BaseMPNotification
  | DisplayValuePropositionNotification;

export enum ChestLockedType {
  TimeLocked = 'TIME_LOCKED',
  IceLocked = 'ICE_LOCKED'
}

export interface ForceLogoutNotification {
  msg?: string;
}

export interface GeoComplyPLC {
  id: string;
  key: string;
  reason: string;
  result: number;
}

export interface SuspendAccount {
  msg: string;
  text: string;
  notifyId: number;
  code: SuspendAccountCode;
}

export interface ForceLogout {
  forceLogout: boolean;
  msg: string;
  reasonCode: number;
}
